/** Record identity shared by invalidation, mounted readers and manual refresh.
 * Unknown shapes deliberately keep the existing domain-wide correctness fallback.
 */
export const RESOURCE_FAMILIES = ['live','purchase','product','catalog','supplier','order','setting','dictionary'] as const;
type Family = typeof RESOURCE_FAMILIES[number];
export type ReadScope = {family:Family; id?:string; projection?:string};
export type ChangeScope = Partial<Record<Family,string[]>>;
const pathScope=(url:string):ReadScope|undefined=>{
    const path=url.split('?')[0];
    if(/\/(?:supplier\/brands|erp\/product-brands)$/.test(path))return {family:'dictionary',id:'0'};
    if(/\/supplier\/product-series$/.test(path))return {family:'dictionary',id:new URLSearchParams(url.split('?')[1]??'').get('brand_id')??undefined};
    if(/\/product-prices$/.test(path)){
        const params=new URLSearchParams(url.split('?')[1]??'');
        for(const [parameter,family] of [['product_master_id','product'],['supplier_product_id','catalog']] as const){
            const id=params.get(parameter);if(id && /^\d+$/.test(id))return {family,id};
        }
    }
    const patterns:Array<[RegExp,Family]>=[
        [/\/livestreams(?:\/(\d+))?(?:\/|$)/,'live'],
        [/\/procurements(?:\/(\d+))?(?:\/|$)/,'purchase'],
        [/\/returned-orders(?:\/(\d+))?(?:\/|$)/,'order'],
        [/\/supplier\/(?:team\/)?suppliers(?:\/(\d+))?(?:\/|$)/,'supplier'],
        [/\/supplier\/products(?:\/(\d+))?(?:\/|$)/,'catalog'],
        [/\/supplier-products(?:\/(\d+))?(?:\/|$)/,'catalog'],
        [/\/(?:inventory|product\/masters)(?:\/(\d+))?(?:\/|$)/,'product'],
    ];
    for(const [pattern,family] of patterns){const match=path.match(pattern);if(match)return {family,id:match[1],projection:new URLSearchParams(url.split('?')[1]??'').get('view')??undefined};}
};
export function readScope(key:string):ReadScope|undefined {
    if(key.startsWith('raw|'))return pathScope(key.slice(4));
    const parts=key.replace(/^(?:view|page)\|/,'').split('|');
    const [domain,projection,id]=parts;
    if(domain==='suppliers'&&projection==='catalog-names')return {family:'dictionary',id:id==='brands'?'0':id};
    if(domain==='live' && ['screen','statistics','ai-prefill','similar','information','sessions','preparation','performance','review'].includes(projection) && /^\d+$/.test(id??''))return {family:'live',id,projection};
    if(domain==='live' && projection==='returned-order' && /^\d+$/.test(id??''))return {family:'order',id};
    if(domain==='live' && ['summary','choices'].includes(projection))return {family:'live',projection};
    if(domain==='purchase' && ['screen','statistics','information','header'].includes(projection) && /^\d+$/.test(id??''))return {family:'purchase',id,projection:projection==='screen' && ['screen','information'].includes(parts[3])?parts[3]:projection};
    if(domain==='purchase' && projection==='summary')return {family:'purchase',projection};
    if(domain==='inventory')return {family:'product',...(/^\d+$/.test(projection??'')?{id:projection}:{})};
    if(domain==='supplier-detail' && /^\d+$/.test(projection??''))return {family:'supplier',id:projection};
    if(domain==='products' && ['price-history','price-calculation'].includes(projection)){
        const match=id?.match(/^(master|pool):(\d+)$/);if(match)return {family:match[1]==='master'?'product':'catalog',id:match[2]};
    }
    if(domain==='supplier-products' && projection==='window')return {family:'catalog'};
    if(domain==='suppliers' && projection==='directory')return {family:'supplier'};
}
export function mutationScope(url:string,data:any,method:string):ChangeScope|undefined {
    const direct=pathScope(url);
    // Creating an unused name changes choices, not supplier/product records.
    // Renaming is deliberately excluded because it also updates linked pots.
    if(direct?.family==='dictionary'&&direct.id&&method==='POST'&&data?.saved)return {dictionary:[direct.id]};
    if(!direct?.id || !['PATCH','PUT'].includes(method))return undefined;
    const path=url.split('?')[0];
    if(direct.family==='live' && /\/work\/preparation\/title\.text$/.test(path))return {live:[direct.id]};
    // Stock/workflow actions can affect availability in another record of the
    // same family. Narrow only independently owned field edits, never workflows.
    const local=(direct.family==='live'||direct.family==='purchase')
        ? /\/(?:livestreams|procurements)\/\d+$/.test(path) && /[?&]view=fields(?:&|$)/.test(url)
        : /\/(?:fields|save-detail|cost|sale-price)$/.test(path) || direct.family==='supplier' && /\/suppliers\/\d+$/.test(path);
    if(!local)return undefined;
    if(direct.family==='live'||direct.family==='purchase'){
        const fields=Object.keys(data??{}).filter(key=>key!=='_sync');
        const independent=['id','name','title','description','product_pitch_script','number_date','expected_arrival_date','tracking_numbers'];
        if(!fields.length || fields.some(key=>!independent.includes(key)))return undefined;
    }
    const scope:ChangeScope={[direct.family]:[direct.id]};
    // Copy/transfer confirmations must include destinations. If they do not,
    // do not narrow this workflow from the source URL alone.
    if(/\/(?:copy|move|transfer|duplicate)(?:\?|$)/.test(url))return undefined;
    const affected=data?._sync?.affected;
    for(const family of ['live','purchase'] as const) {
        if(Array.isArray(affected?.[family]) && affected[family].every((id:unknown)=>Number.isSafeInteger(id)&&Number(id)>0))
            scope[family]=Array.from(new Set([...(scope[family]??[]),...affected[family].map(String)]));
    }
    return scope;
}
export function scopeAffectsRead(key:string,scope?:ChangeScope):boolean {
    const read=readScope(key);
    if(scope?.dictionary&&read)return read.family==='dictionary'&&(!read.id||scope.dictionary.includes(read.id));
    return !read?.id || !scope?.[read.family] || scope[read.family]!.includes(read.id);
}
export function validChangeScope(value:unknown):value is ChangeScope {
    return !!value && typeof value==='object' && !Array.isArray(value) && Object.entries(value).every(([family,ids])=>
        RESOURCE_FAMILIES.includes(family as Family) && Array.isArray(ids) && ids.length>0 && ids.length<=500 && ids.every(id=>typeof id==='string'&&/^\d+$/.test(id)));
}
